import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  Popup,
  Heading,
  DatePicker,
  Button,
  Preloader,
  I18n,
} from 'components';

import addDays from 'date-fns/add_days';
import { formatDateString } from 'utils/formatString';
import { closePopup } from 'actions';
import { pollingUpdate } from 'utils/polling';
import { pushBlockedCtnActionEvent } from 'utils/ga/home';
import { ymPushParams } from 'utils/analytics/ym';
import { BLOCK_NUMBER_SUCCESS, UNBLOCK_NUMBER_SUCCESS } from 'utils/analytics/constants';

import classNames from 'classnames/bind';
import {
  fetchData,
  toggleBlockedCtnAction,
  cancelFutureStatusRequest,
} from '../../actions';

import styles from './styles.pcss';
const cx = classNames.bind(styles);

const [clearPollingUpdateInterval, wrapIntervalCallback] = pollingUpdate(3000, 5);

const getIsoDate = (input) => {
  if (input.match(/^(\d{4})-(\d{2})-(\d{2})$/)) return input;
  return formatDateString(input, 'YYYY-MM-DD');
};

class ChangeStatusPopups extends Component {
  preventSubmit = false;

  constructor(props) {
    super(props);

    this.state = {
      response: null,
      isLoading: false,
      isValid: true,
      isServerError: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { isServerError, response } = this.state;
    const desiredPopupTypes = ['blockCtn', 'unblockCtn', 'cancelFutureStatusRequest'];

    if (desiredPopupTypes.includes(this.props.activePopup)) {
      const isServerErrorCondition = isServerError && prevState.isServerError !== isServerError;
      const responseCondition = prevState.response !== response;

      if (isServerErrorCondition || responseCondition && response.isSucceeded === false) {
        pushBlockedCtnActionEvent(this.props.activePopup, 'error');
        return;
      }

      if (responseCondition && response.requestId) {
        pushBlockedCtnActionEvent(this.props.activePopup, 'success');
      }
    }
  }

  pollingAction = () => this.props.actions.fetchData(['Status']);

  handleDateChange = (date) => {
    if (date) {
      this.setState({ date, isValid: true }, pushBlockedCtnActionEvent(this.props.activePopup));
    } else {
      this.setState({ date: null, isValid: false });
    }
  };

  formSubmitHandler = (event) => {
    const { activePopup: actionType } = this.props;

    event.preventDefault();

    const date = getIsoDate(event.target.elements.date.value);

    if (this.preventSubmit || !date || date === 'Invalid Date') {
      this.preventSubmit = false;
      return;
    }

    this.setState({ isLoading: true });

    if (actionType === 'blockCtn') {
      // eslint-disable-next-line camelcase
      ymPushParams({ lk_ev_: BLOCK_NUMBER_SUCCESS });
      this.handleBlockSubmit(date);
    }

    if (actionType === 'unblockCtn') {
      // eslint-disable-next-line camelcase
      ymPushParams({ lk_ev_: UNBLOCK_NUMBER_SUCCESS });
      this.handleUnblockSubmit(date);
    }
  };

  handleBlockSubmit = (date) => {
    const blockAction = wrapIntervalCallback(
      this.pollingAction,
      this.props.actions.toggleBlockedCtnAction,
    );

    blockAction(date, this.props.activePopup)
      .then((data) => {
        this.setState({ response: data, isLoading: false });
      })
      .catch(() => {
        this.setState({ isServerError: true });
      });
  };

  handleUnblockSubmit = (date) => {
    const unblockAction = wrapIntervalCallback(
      this.pollingAction,
      this.props.actions.toggleBlockedCtnAction,
    );

    unblockAction(date, this.props.activePopup)
      .then((data) => {
        this.setState({ response: data, isLoading: false });
      })
      .catch(() => {
        this.setState({ isServerError: true });
      });
  };

  handleFutureRequestCancelation = () => {
    this.setState({ isLoading: true });

    const cancelAction = wrapIntervalCallback(
      this.pollingAction,
      this.props.actions.cancelFutureStatusRequest,
    );

    cancelAction('BLK')
      .then((data) => {
        this.setState({ response: data, isLoading: false });
      })
      .catch(() => {
        this.setState({ isServerError: true });
      });
  };

  handleFutureTariffCancelation = () => {
    this.setState({ isLoading: true });

    const cancelAction = wrapIntervalCallback(
      this.pollingAction,
      this.props.actions.cancelFutureStatusRequest,
    );

    cancelAction('CPP')
      .then((data) => {
        this.setState({ response: data, isLoading: false });
      })
      .catch(() => {
        this.setState({ isServerError: true });
      });
  };

  handleClose = async () => {
    await this.props.actions.closePopup();

    this.setState({
      response: null,
      date: undefined,
      isServerError: false,
    });
  };

  onValidStateChange = (isValid, delay) => () => {
    setTimeout(() => {
      this.setState({ isValid });
    }, delay);
  };

  render() {
    const { activePopup, isConvergent } = this.props;
    const { date, response, isLoading, isServerError } = this.state;
    const semiSuccess = response?.status === 'semisuccess';

    if (response && response.isSucceeded === false || isServerError) {
      return (
        <Popup
          onClose={this.handleClose}
          opened
        >
          <div className={cx('message')}>
            <Heading className={cx('title')} level={1}>
              Упс, что-то пошло не так
            </Heading>
            <Heading className={cx('subtitle')} level={5}>
              При выполнении операции произошла ошибка. Попробуйте еще раз
            </Heading>
            <Button
              className={['big', cx('button')]}
              onClick={this.handleClose}
            >
              Закрыть
            </Button>
          </div>
        </Popup>
      );
    }

    if (activePopup === 'blockCtn') {
      return (
        <Popup
          onClose={this.handleClose}
          opened
        >
          {!response ? (
            <form
              className={cx('form', { isLoading })}
              onSubmit={this.formSubmitHandler}
            >
              <Heading className={cx('title')} level={1}>
                <I18n path="home.summary.blockButton" />
              </Heading>
              {isLoading ? (
                <Preloader
                  className={cx('preloader')}
                  size="80"
                />
              ) : (
                <>
                  {isConvergent && (
                    <Heading className={cx('subtitle')} level={5}>
                      Блокировка приведет к приостановлению оказания всех услуг
                      {' '}
                      Мобильной связи, Домашнего Интернета и Телевидения
                    </Heading>
                  )}

                  <Heading className={cx('formDescription')} level={5}>
                    Введите дату блокировки и мы заблокируем номер
                  </Heading>

                  <DatePicker
                    className={cx('input')}
                    name="date"
                    size="big"
                    value={date}
                    min={new Date()}
                    onChange={this.handleDateChange}
                    onFocus={this.onValidStateChange(false, 0)}
                    onBlur={this.onValidStateChange(true, 100)}
                    onEnterPress={() => { this.preventSubmit = true; }}
                  />
                  <Button
                    className={['big', cx('button')]}
                    type="submit"
                    disabled={!this.state.isValid}
                  >
                    <I18n path="home.summary.blockButton" />
                  </Button>
                </>
              )}
            </form>
          ) : (
            <div className={cx('message')}>
              <Heading className={cx('title')} level={1}>
                <div dangerouslySetInnerHTML={{ __html:
                    semiSuccess ? response.title
                      : 'Запрос на блокировку<br />успешно отправлен' }}
                />
              </Heading>
              <Heading className={cx('subtitle')} level={5}>
                <div dangerouslySetInnerHTML={{ __html:
                    semiSuccess ? response.message
                      : `Запрос на блокировку ${response.requestId !== -1 && (`№ ${response.requestId}`)}
                    успешно отправлен` }}
                />
              </Heading>
              <Button
                className={['big', cx('button')]}
                onClick={this.handleClose}
              >
                Продолжить работу
              </Button>
            </div>
          )}
        </Popup>
      );
    }

    if (activePopup === 'unblockCtn') {
      return (
        <Popup
          onClose={this.handleClose}
          opened
        >
          {!response ? (
            <form
              className={cx('form', { isLoading })}
              onSubmit={this.formSubmitHandler}
            >
              <Heading className={cx('title')} level={1}>
                <I18n path="home.summary.unblockButton" />
              </Heading>
              {isLoading ? (
                <Preloader
                  className={cx('preloader')}
                  size="80"
                />
              ) : (
                <>
                  <Heading className={cx('subtitle')} level={5}>
                    Введите дату разблокировки и мы разблокируем номер
                  </Heading>
                  <DatePicker
                    className={cx('input')}
                    name="date"
                    size="big"
                    value={date}
                    min={new Date()}
                    max={addDays(new Date(), 30)}
                    onChange={this.handleDateChange}
                    onFocus={this.onValidStateChange(false, 0)}
                    onBlur={this.onValidStateChange(true, 100)}
                    onEnterPress={() => { this.preventSubmit = true; }}
                  />
                  <Button
                    className={['big', cx('button')]}
                    type="submit"
                    disabled={!this.state.isValid}
                  >
                    <I18n path="home.summary.unblockButton" />
                  </Button>
                </>
              )}
            </form>
          ) : (
            <div className={cx('message')}>
              <Heading className={cx('title')} level={1}>
                <div dangerouslySetInnerHTML={{ __html:
                    semiSuccess ? response.title
                      : 'Запрос на разблокировку<br />успешно отправлен' }}
                />
              </Heading>

              <Heading className={cx('subtitle')} level={5}>
                <div dangerouslySetInnerHTML={{ __html:
                    semiSuccess ? response.message
                      : `Запрос на разблокировку ${response.requestId !== -1 && (`№ ${response.requestId}`)}
                    успешно отправлен` }}
                />
              </Heading>

              <Button
                className={['big', cx('button')]}
                onClick={this.handleClose}
              >
                Продолжить работу
              </Button>
            </div>
          )}
        </Popup>
      );
    }

    if (activePopup === 'cancelFutureStatusRequest') {
      return (
        <Popup
          onClose={this.handleClose}
          opened
        >
          {!response ? (
            <div className={cx('message', { isLoading })}>
              <Heading className={cx('title')} level={1}>
                Отмена статуса будущей блокировки/разблокировки
              </Heading>
              {isLoading ? (
                <Preloader
                  className={cx('preloader')}
                  size="80"
                />
              ) : (
                <>
                  <Heading className={cx('subtitle')} level={5}>
                    Вы уверены, что хотите отменить статус будущей блокировки/разблокировки?
                  </Heading>
                  <Button
                    className={['big', cx('button')]}
                    onClick={this.handleFutureRequestCancelation}
                  >
                    Да, уверен
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className={cx('message')}>
              <Heading className={cx('title')} level={1}>
                Запрос успешно отправлен
              </Heading>
              <Heading className={cx('subtitle')} level={5}>
                Запрос на отмену статуса будущей блокировки/разблокировки
                {' '}
                №
                {response.requestId}
                {' '}
                успешно отправлен
              </Heading>
              <Button
                className={['big', cx('button')]}
                onClick={this.handleClose}
              >
                Продолжить работу
              </Button>
            </div>
          )}
        </Popup>
      );
    }

    if (activePopup === 'cancelFutureTariff') {
      return (
        <Popup
          onClose={this.handleClose}
          opened
        >
          {!response ? (
            <div className={cx('message', { isLoading })}>
              <Heading className={cx('title')} level={1}>
                Отмена смены тарифа
              </Heading>
              {isLoading ? (
                <Preloader
                  className={cx('preloader')}
                  size="80"
                />
              ) : (
                <>
                  <Heading className={cx('subtitle')} level={5}>
                    Вы уверены, что хотите отменить переход на новый тариф?
                  </Heading>
                  <Button
                    className={['big', cx('button')]}
                    onClick={this.handleFutureTariffCancelation}
                  >
                    Да, уверен
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className={cx('message')}>
              <Heading className={cx('title')} level={1}>
                Запрос успешно отправлен
              </Heading>
              <Heading className={cx('subtitle')} level={5}>
                Запрос на отмену перехода на новый тариф
                {' '}
                №
                {response.requestId}
                {' '}
                успешно отправлен
              </Heading>
              <Button
                className={['big', cx('button')]}
                onClick={this.handleClose}
              >
                Продолжить работу
              </Button>
            </div>
          )}
        </Popup>
      );
    }

    return null;
  }
}

ChangeStatusPopups.propTypes = {
  actions: PropTypes.object,
  activePopup: PropTypes.string,
  isConvergent: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  activePopup: state.application.popup,
  isConvergent: state.application.isConvergent,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    closePopup,
    fetchData,
    toggleBlockedCtnAction,
    cancelFutureStatusRequest,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChangeStatusPopups);
